import camelize from 'camelize';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';

// LOGGI
import Button from '@loggi/ui/components/button';
import Col, { SIZES as COL_SIZES } from '@loggi/ui/components/col';
import colors from '@loggi/ui/components/utils/colors';
import Display from '@loggi/ui/components/display';
import googleMapsAddressSource from '@loggi/ui/components/typeahead/sources/google-maps-address';
import Icon from '@loggi/ui/components/icon';
import icons from '@loggi/ui/components/utils/icons';
import Map from '@loggi/ui/components/map';
import MapMarker from '@loggi/ui/components/map-marker';
import normalizeAddressData, {
  getAddressData
} from '@loggi/js/address/normalize-address';
import screens from '@loggi/ui/components/utils/screens';
import Text from '@loggi/ui/components/text';
import TextInput from '@loggi/ui/components/text-input';
import Typeahead from '@loggi/ui/components/typeahead';
import View from '@loggi/ui/components/view';
import { addressGeocode } from '@loggi/address-geocoder';

// UTILS
import parseGeocodeFromAddress from '../utils/parse-geocode-from-address';
import { addressHasNumber, noop } from '../utils/address';
import {
  abbreviateWords,
  getSuggestionValue,
  NUMBER_MASK,
  renderSuggestion
} from '../utils/suggestion';

import Disambiguation from '../components/disambiguation';
import styles from './styles.scss';
import { SIZES } from '../types';

const ADDRESS_EXAMPLE_PLACEHOLDER = 'Ex: Av. Paulista, 900';
const ADDRESS_EXAMPLE = `${ADDRESS_EXAMPLE_PLACEHOLDER} - Bela Vista, São Paulo - SP, 01310-100, Brasil`;
const ADDRESS_NOT_EXISTS = 'Endereço não encontrado';
const CHOOSE_ONE_MESSAGE = 'Escolha um endereço para continuar';
const DANGER_STATUS = 'danger';
const MOBILE_ADDRESS_ZOOM = 16;
const MOBILE_BUTTON_TEXT = 'OK';
const MOBILE_CITY_ZOOM = 12;
const NOT_NUMBER = 'Você precisa informar um número';
const NUMBER_NOT_EXISTS = 'Número não existe';
const SP_LOCATION = { lat: -23.522446, lng: -46.740824 };
const WARNING_STATUS = 'warning';

const isSmallScreen = () => screens.getScreen() < screens.sizes.md;

class AddressTypeahead extends React.Component {
  static displayName = 'AddressTypeahead';

  static propTypes = {
    bounds: PropTypes.array,
    placeholder: PropTypes.string,
    initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    label: PropTypes.string,
    icon: PropTypes.oneOf(icons.names),
    inputProps: PropTypes.object,
    markerProps: PropTypes.object,
    dataSources: PropTypes.array,
    size: PropTypes.oneOf(SIZES),
    hasFocus: PropTypes.bool,
    disableNumberValidation: PropTypes.bool,
    preventKeyEvent: PropTypes.bool,
    gmapsSourceInput: PropTypes.func,
    onSuggestionsOpen: PropTypes.func,
    onSuggestionsClose: PropTypes.func,
    onChange: PropTypes.func,
    onAddressSelected: PropTypes.func,
    customRenderAddress: PropTypes.func,
    renderValue: PropTypes.func,
    /*
     * This prop controls the activation/deactivation of google maps places in maps prediction
     */
    placesEnabled: PropTypes.bool
  };

  static defaultProps = {
    onChange: noop,
    onAddressSelected: noop,
    bounds: null,
    initialValue: null,
    icon: null,
    inputProps: {},
    markerProps: {},
    dataSources: [],
    preventKeyEvent: false,
    placeholder: ADDRESS_EXAMPLE_PLACEHOLDER,
    customRenderAddress: null,
    renderValue: null,
    placesEnabled: false
  };

  constructor(props) {
    super(props);

    this._onSuggestionSelected = this._onSuggestionSelected.bind(this);
    this._onDisambiguationSelected = this._onDisambiguationSelected.bind(this);
    this._googleMapsSource = this._googleMapsSource.bind(this);
    this._onChange = this._onChange.bind(this);
    this._parseQuery = this._parseQuery.bind(this);
    this._renderValue = this._renderValue.bind(this);
    this._getSuggestionValue = this._getSuggestionValue.bind(this);
    this._addressRender = this._addressRender.bind(this);
    this._listRender = this._listRender.bind(this);
    this._mobileContent = this._mobileContent.bind(this);
    this._renderTheme = this._renderTheme.bind(this);
    this._mobileClose = this._mobileClose.bind(this);
    this._getStatus = this._getStatus.bind(this);

    const initialValue = this._parseInitialValue(props.initialValue);
    this._lastSuggestionIndex = null;
    this._timeout = null;
    this.state = {
      geocodes: initialValue ? [initialValue] : [],
      sessionToken: null,
      suggestion: null,
      missingAddressNumber: false,
      numberNotFound: false,
      addressNotFound: false,
      value: null,
      mobileOpened: false,
      mobileList: null
    };
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.initialValue !== this.props.initialValue) {
      const value = this._parseInitialValue(nextProps.initialValue);
      this.setState({
        geocodes: value ? [value] : []
      });
    }
  }

  componentWillUnmount() {
    this._clearTimeout();
  }

  _addressRender(Input, state, actions, inputProps) {
    const { isFocused, mobileOpened } = this.state;
    const { customRenderAddress } = this.props;

    const hasCustom = typeof customRenderAddress === 'function';

    if (isSmallScreen() && hasCustom) {
      const customProps = this._getCustomInputProps(inputProps, isFocused);
      const renderConfig = {
        onClose: () => this._mobileClose(actions),
        title: <Input {...customProps} />
      };

      const updateMobileContent = isFocused !== mobileOpened && isSmallScreen();
      if (updateMobileContent) {
        renderConfig.content = this._mobileContent(null);
      }

      let newOpened = !mobileOpened;
      const push = isFocused ? newOpened : false;
      customRenderAddress(renderConfig, push);

      if (newOpened && isFocused) {
        this.setState({ mobileOpened: newOpened });
      }
    }

    return true;
  }

  _clearTimeout() {
    if (this._timeout) {
      clearTimeout(this._timeout);
      this._timeout = null;
    }
  }

  _getCustomInputProps(inputProps, focused) {
    const customProps = {
      ...inputProps,
      disableBorder: true,
      displayIcon: false,
      displayStatus: false,
      hasFocus: Boolean(focused),
      inverse: true,
      label: null,
      ref: null
    };

    if (!focused) customProps.focus = null;

    return customProps;
  }

  _getStatus() {
    const {
      value,
      missingAddressNumber,
      addressNotFound,
      numberNotFound
    } = this.state;

    let status = null;
    let statusText = null;
    let focus = null;

    if (missingAddressNumber) {
      status = WARNING_STATUS;
      statusText = NOT_NUMBER;

      const hasNumberMask = value && value.indexOf(NUMBER_MASK) > -1;
      if (hasNumberMask) {
        const start = value.indexOf(NUMBER_MASK);
        const end = start + NUMBER_MASK.length;
        focus = { start, end };
      }
    } else if (addressNotFound) {
      status = DANGER_STATUS;
      statusText = ADDRESS_NOT_EXISTS;
    } else if (numberNotFound) {
      status = DANGER_STATUS;
      statusText = NUMBER_NOT_EXISTS;
    }

    return { status, statusText, focus };
  }

  _getSuggestionValue(suggestion) {
    const disableNumberValidation = this.props.disableNumberValidation;
    return getSuggestionValue(suggestion, disableNumberValidation);
  }

  _googleMapsSource(value, updateSuggestions) {
    const { bounds, placesEnabled } = this.props;

    const { sessionToken } = this.state;
    const input = this.props.gmapsSourceInput
      ? this.props.gmapsSourceInput(value)
      : value;

    return googleMapsAddressSource(
      input,
      updateSuggestions,
      bounds,
      placesEnabled,
      sessionToken
    );
  }

  _listRender(List) {
    const customRenderAddress = this.props.customRenderAddress;
    const hasCustom = typeof customRenderAddress === 'function';
    const mobileOpened = this.state.mobileOpened;

    if (isSmallScreen() && mobileOpened && hasCustom) {
      const content = this._mobileContent(List);
      customRenderAddress({ content });
    }

    return true;
  }

  _mobileClose(actions) {
    const inputBlurred = actions.inputBlurred;
    const missingAddressNumber = this.state.missingAddressNumber;

    if (typeof inputBlurred === 'function') {
      inputBlurred();
    }

    let newState = {
      mobileOpened: false,
      missingAddressNumber: false
    };

    if (missingAddressNumber) {
      newState.geocodes = [];
      newState.suggestion = null;
      newState.value = null;
    }

    this.setState(newState);
  }

  _mobileContent(mobileList, forceGeocodes = null) {
    const { customRenderAddress, markerProps } = this.props;
    const hasCustom = typeof customRenderAddress === 'function';
    const geocodes = forceGeocodes || this.state.geocodes;
    const hasGeocode = geocodes && geocodes.length > 0;
    const { status, statusText } = this._getStatus();
    const hasStatus = status !== null && statusText !== null;

    let renderMarker = false;
    let location = SP_LOCATION;

    if (hasGeocode) {
      const [geocode] = geocodes;
      const normalizedAddress = normalizeAddressData(geocode) || {};
      const geometry = normalizedAddress.geometry || {};
      const addressLocation = geometry.location || {};
      const lat = addressLocation.lat || null;
      const lng = addressLocation.lng || null;

      if (lat && lng) {
        renderMarker = true;
        location = { lat, lng };
      }
    }

    const marker =
      hasGeocode && renderMarker ? (
        <MapMarker {...markerProps} {...location} />
      ) : null;

    const zoom = marker ? MOBILE_ADDRESS_ZOOM : MOBILE_CITY_ZOOM;

    const buttonClick = () => {
      if (hasCustom) {
        customRenderAddress({}, true, false);
      }
    };

    const statusClasses = classNames(
      styles.mobileStatus,
      { [styles.border]: Boolean(mobileList) },
      { [styles.warning]: status === WARNING_STATUS },
      { [styles.danger]: status === DANGER_STATUS }
    );

    return (
      <View className={styles.mobileContent}>
        <Display when={hasStatus}>
          <Col
            size={COL_SIZES.COL_12}
            gutter={{ top: 'half', bottom: 'half', right: 'normal' }}
            className={statusClasses}
          >
            <Text>
              <Icon
                className={styles.mobileStatusIcon}
                type={TextInput.ICON_STATUS_MAPPER[status]}
              />
            </Text>
            <Text bold small>
              {statusText}
            </Text>
          </Col>
        </Display>
        {mobileList}
        <Display when={!mobileList && !hasStatus}>
          <View className={styles.mobileMap}>
            <Map
              initialCenter={location}
              center={location}
              initialZoom={zoom}
              zoom={zoom}
            >
              {marker}
            </Map>
          </View>
        </Display>
        <Display when={!mobileList && !hasStatus}>
          <Button
            block
            size="large"
            className={styles.buttonConfirm}
            onSelect={buttonClick}
            value={MOBILE_BUTTON_TEXT}
            disabled={!hasGeocode}
          />
        </Display>
      </View>
    );
  }

  _onChange(value) {
    const suggestion = this.state.suggestion;
    const geocodes = this.state.geocodes;
    const onChange = this.props.onChange;
    const mobileOpened = this.state.mobileOpened;

    if (geocodes && geocodes.length === 1) {
      const [address] = geocodes;
      const renderDifferent = this._renderValue() !== value;

      if (address && renderDifferent) {
        this.setState({
          geocodes: [],
          value: value
        });

        onChange(null);
      }
    }

    if (suggestion) {
      const { mainText } = suggestion.structuredFormatting;
      const abbreviatedValue = abbreviateWords(value) || '';
      const abbreviatedMain = abbreviateWords(mainText);
      const startsWithMain = abbreviatedValue.startsWith(abbreviatedMain);

      if (value && !startsWithMain) {
        this.setState({
          suggestion: null,
          value: null,
          missingAddressNumber: false
        });

        if (onChange) {
          onChange(null);
        }
      } else {
        this.setState({
          value
        });
      }
    }

    if (!mobileOpened && isSmallScreen()) {
      this.setState({
        mobileOpened: true
      });
    }
  }

  _onDisambiguationSelected(geocode) {
    if (geocode) {
      const value = this.state.value;
      this._parseGeocode(value, [geocode]);
    } else {
      const onChange = this.props.onChange;
      this.setState({ geocodes: [] });
      if (onChange) {
        onChange(null);
      }
    }
  }

  _onSuggestionSelected(s, { suggestionIndex }) {
    const { bounds } = this.props;
    const suggestion = this._getSuggestionValue(s);
    const disableNumberValidation = this.props.disableNumberValidation;
    this._lastSuggestionIndex = suggestionIndex;
    this._clearTimeout();

    if (s.data) {
      return this._parseGeocode(suggestion, [s.data]);
    }

    // Before geocode we need to make sure we have a number
    const isPremise = s.types && s.types.includes('premise');
    if (
      suggestion &&
      !addressHasNumber(suggestion) &&
      !disableNumberValidation &&
      !isPremise
    ) {
      this.setState({
        missingAddressNumber: true,
        suggestion: s,
        value: suggestion
      });

      return false;
    }

    this.setState({
      missingAddressNumber: false,
      suggestion: s,
      value: suggestion
    });

    addressGeocode(
      s.placeId ? { placeId: s.placeId } : suggestion,
      this._parseGeocode.bind(this, suggestion),
      bounds
    );

    return true;
  }

  _parsePlaceDetails(value, place) {
    // Address typeahead looks for responses with keys in the camel case format.
    // See _renderValue and _renderAddressInfo
    this._parseGeocode(value, [camelize(place)]);
  }

  _parseGeocode(value, geocodes) {
    const { onAddressSelected, onChange, disableNumberValidation } = this.props;

    if (geocodes.length > 1) {
      this.setState({
        addressNotFound: false,
        numberNotFound: false,
        suggestion: null,
        geocodes
      });

      return false;
    }

    // Validate address here
    const [address] = geocodes;
    if (!address) {
      onChange(null);
      this.setState({ addressNotFound: true });
      return false;
    }

    // Address could be a json, lets handle that here Eg. Favorite Address
    const geocode = parseGeocodeFromAddress(address);

    // Only pick addresData fields to normalize the result.
    const addressData = getAddressData(geocode);

    const hasNumber = addressHasNumber(addressData);

    if (!hasNumber && !disableNumberValidation) {
      onChange(null);
      this.setState({ numberNotFound: true });
      return false;
    }

    if (onAddressSelected) {
      const suggestionIndex = this._lastSuggestionIndex;
      onAddressSelected(addressData, { hasNumber, value, suggestionIndex });
    }

    if (onChange) {
      onChange(addressData);
    }

    const { customRenderAddress } = this.props;
    const hasCustom = typeof customRenderAddress === 'function';

    this.setState({
      addressNotFound: false,
      numberNotFound: false,
      mobileOpened: false,
      geocodes: [addressData],
      suggestion: null
    });

    if (isSmallScreen() && hasCustom) {
      const content = this._mobileContent(null, [addressData]);
      customRenderAddress({ content });
    }

    return true;
  }

  _parseInitialValue(value) {
    return typeof value === 'object' ? value : null;
  }

  _parseQuery(value) {
    const suggestion = this.state.suggestion || null;

    if (suggestion) {
      const { secondaryText } = suggestion.structuredFormatting;
      return `${value} - ${secondaryText}`;
    }

    return value;
  }

  _renderAddressInfo() {
    const disableNumberValidation = this.props.disableNumberValidation;

    if (disableNumberValidation) {
      return null;
    }

    const { geocodes, suggestion } = this.state;
    let infoText = null;
    if (geocodes && geocodes.length === 1) {
      const [geocode] = geocodes;
      const addressComponents = geocode.addressComponents;
      const info = addressComponents.filter(
        ({ types }) =>
          types.indexOf('political') > -1 &&
          types.indexOf('country') === -1 &&
          types.indexOf('administrative_area_level_2') === -1
      );

      infoText = abbreviateWords(info.map(i => i.shortName).join(', '));
    } else if (suggestion) {
      const structuredFormatting = suggestion.structuredFormatting || {};
      const secondaryText = structuredFormatting.secondaryText || '';

      infoText = abbreviateWords(secondaryText);
    }

    if (infoText) {
      const [first, ...last] = infoText.split(',');

      return (
        <View className={styles.info}>
          <span>{first}</span>
          <strong> - {last.join(', ').replace(' -', ',')}</strong>
        </View>
      );
    }

    return null;
  }

  _renderTheme(typeaheadTheme) {
    const customRenderAddress = this.props.customRenderAddress;
    const hasCustom = typeof customRenderAddress === 'function';
    const { status, statusText } = this._getStatus();
    const hasStatus = status !== null && statusText !== null;

    const suggestionsContainer = classNames(
      typeaheadTheme.suggestionsContainer,
      styles.mobileSuggestionsContainer,
      { [styles.hasStatus]: hasStatus }
    );

    const suggestion = classNames(
      typeaheadTheme.suggestion,
      styles.mobileSuggestionsItem
    );

    const addressTheme =
      isSmallScreen() && hasCustom
        ? {
            ...typeaheadTheme,
            suggestionsContainer,
            suggestionFocused: null,
            suggestion
          }
        : typeaheadTheme;

    return addressTheme;
  }

  _renderValue(value, mask = NUMBER_MASK) {
    const geocodes = this.state.geocodes;
    const renderValue = this.props.renderValue;
    const renderIsFunction = typeof renderValue === 'function';

    if (geocodes && geocodes.length === 1) {
      const [geocode] = geocodes;
      const addressComponents = geocode.addressComponents;
      const disableNumberValidation = this.props.disableNumberValidation;

      const route = addressComponents.find(
        ({ types }) => types[0] === 'route'
      ) || { shortName: '' };

      const streetNumber = addressComponents.find(
        ({ types }) => types[0] === 'street_number'
      ) || { shortName: '' };

      if (disableNumberValidation) {
        return abbreviateWords(geocode.formattedAddress);
      }

      return abbreviateWords(
        `${route.shortName}, ${streetNumber ? streetNumber.shortName : mask}`
      );
    } else if (renderIsFunction) {
      return renderValue(value);
    }

    return value;
  }

  render() {
    const {
      inputProps: originalInputProps,
      label,
      icon,
      size,
      hasFocus,
      preventKeyEvent,
      disableNumberValidation
    } = this.props;

    const { geocodes } = this.state;
    const { status, statusText, focus } = this._getStatus();

    let inputProps = Object.assign({}, originalInputProps);
    inputProps.statusInfo = this._renderAddressInfo();

    if (statusText !== null) {
      inputProps.statusText = statusText;
    }

    if (status !== null) {
      inputProps.status = status;
    }

    if (focus !== null) {
      inputProps.focus = focus;
    }

    let auxiliarComponent = null;

    if (!disableNumberValidation) {
      const smallScreen = isSmallScreen();
      auxiliarComponent = (
        <View
          className={classNames(styles.auxiliarComponent, {
            small: smallScreen
          })}
        >
          {smallScreen && (
            <Text>
              <Icon
                type={icons.type.INFO}
                color={colors.type.INFO}
                className={styles.chooseOneMessageIcon}
              />
            </Text>
          )}
          <Text small bold>
            {smallScreen ? CHOOSE_ONE_MESSAGE : ADDRESS_EXAMPLE}
          </Text>
        </View>
      );
    }

    return (
      <View className={styles.container}>
        <Typeahead
          icon={icon}
          label={label}
          parseQuery={this._parseQuery}
          renderValue={this._renderValue}
          getSuggestionValue={this._getSuggestionValue}
          renderSuggestion={renderSuggestion}
          placeholder={this.props.placeholder}
          auxiliarComponent={auxiliarComponent}
          selectFirst
          ref="address"
          onChange={this._onChange}
          dataSources={[...this.props.dataSources, this._googleMapsSource]}
          onSuggestionSelected={this._onSuggestionSelected}
          onSuggestionsOpen={this.props.onSuggestionsOpen}
          onSuggestionsClose={this.props.onSuggestionsClose}
          inputProps={inputProps}
          size={size}
          hasFocus={hasFocus}
          preventKeyEvent={preventKeyEvent}
          customRenderInput={this._addressRender}
          customRenderList={this._listRender}
          theme={this._renderTheme}
          validateInputToAutocomplete
        />
        {geocodes.length > 1 && (
          <Disambiguation
            geocodes={geocodes}
            onDisambiguationSelected={this._onDisambiguationSelected}
          />
        )}
      </View>
    );
  }
}

export default AddressTypeahead;
